import { useTranslation } from 'react-i18next';
import type { SettingCategory } from '../types/setting';

interface Tab {
  category: SettingCategory;
  label: string;
  itemCount: number;
}

interface SettingCategoryTabsProps {
  tabs: Tab[];
  activeCategory: SettingCategory;
  onChange: (category: SettingCategory) => void;
}

const CATEGORY_ICONS: Record<SettingCategory, string> = {
  SECURITY: '🛡',
  PERFORMANCE: '⚡',
  APPEARANCE: '🎨',
  LOGGING: '📄',
};

export function SettingCategoryTabs({
  tabs,
  activeCategory,
  onChange,
}: SettingCategoryTabsProps) {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t('systemSettings.list.title')}
      style={{
        display: 'flex',
        gap: 'var(--spacing-1)',
        borderBottom: '2px solid var(--color-surface-border)',
        marginBottom: 'var(--spacing-4)',
        overflowX: 'auto',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.category === activeCategory;
        return (
          <button
            key={tab.category}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.category}`}
            id={`tab-${tab.category}`}
            type="button"
            onClick={() => onChange(tab.category)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(tab.category);
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-1)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'transparent',
              border: 'none',
              borderBottom: isActive
                ? '2px solid var(--color-brand-primary)'
                : '2px solid transparent',
              marginBottom: '-2px',
              color: isActive ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-family-sans)',
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            <span aria-hidden="true">{CATEGORY_ICONS[tab.category]}</span>
            {tab.label}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                height: 20,
                padding: '0 var(--spacing-1)',
                borderRadius: 'var(--radius-full)',
                background: isActive
                  ? 'var(--color-brand-primary)'
                  : 'var(--color-surface-border)',
                color: isActive ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
              }}
            >
              {tab.itemCount}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Skeleton version for the loading state */
export function SettingCategoryTabsSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        gap: 'var(--spacing-1)',
        borderBottom: '2px solid var(--color-surface-border)',
        marginBottom: 'var(--spacing-4)',
      }}
    >
      {[80, 70, 75, 65].map((w, i) => (
        <div
          key={i}
          style={{
            width: w,
            height: 36,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-elevated)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}
